import { call, put, takeLatest } from "redux-saga/effects";
import {
  deleteQuestionRequest,
  deleteQuestionSuccess,
  deleteQuestionFailure,
  addQuestionRequest,
  addQuestionSuccess,
  addQuestionFailure,
  updateQuestionRequest,
  updateQuestionSuccess,
  updateQuestionFailure,
  fetchQuestionsRequest,
  fetchQuestionsSuccess,
  fetchQuestionsFailure,
  updateAnswerRequest,
  updateAnswerSuccess,
  updateAnswerFailure,
  resetQuestions,
  addAnswerRequest,
  addAnswerSuccess,
  addAnswerFailure,
  deleteAnswerRequest,
  deleteAnswerSuccess,
  deleteAnswerFailure,
  addChildQuestionSuccess,
  addChildQuestionFailure,
  addChildQuestionRequest,
  updateChildQuestionSuccess,
  updateChildQuestionFailure,
  updateChildQuestionRequest,
  deleteChildQuestionSuccess,
  deleteChildQuestionFailure,
  deleteChildQuestionRequest,
  updateChildAnswerRequest,
  updateChildAnswerSuccess,
  updateChildAnswerFailure,
} from "../slices/questionSlice";
import {
  fetchExamSuccess
} from "../slices/examSlice";
import { apiRequestWithAuth } from "../utils/api";
import { PayloadAction } from "@reduxjs/toolkit";
import {Question, QuestionTypes} from "../types/Question";
import {ApiResponse, User} from "../types";
import {fetchUsersFailure, fetchUsersSuccess} from "../slices/userSlice";

// API Delete Question
function* handleDeleteQuestion(action: PayloadAction<{ id: string; examId: string }>) {
  try {
    const response: ApiResponse<Question> = yield call(apiRequestWithAuth, `/quiz/${action.payload.examId}/question/${action.payload.id}`, "DELETE");

    if (response.messageCode != 200) throw new Error("Failed to delete question");

    yield put(deleteQuestionSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteQuestionFailure(error.message));
  }
}
function* handleDeleteChildQuestion(action: PayloadAction<{ id: string; uid: string; examId: string }>) {
  try {
    const response: ApiResponse<Question> = yield call(apiRequestWithAuth, `/quiz/${action.payload.examId}/question/${action.payload.id}`, "DELETE");

    if (response.messageCode != 200) throw new Error("Failed to delete question");

    yield put(deleteChildQuestionSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteChildQuestionFailure(error.message));
  }
}

// API Add Question
function* handleAddQuestion(action: PayloadAction<any>) {
  try {
    const payload = action.payload;
    const response: ApiResponse<Question> = yield call(apiRequestWithAuth, `/quiz/${payload.examId}/question`, "POST", payload.data);

    if (response.messageCode != 200) throw new Error("Failed to add question");

    yield put(addQuestionSuccess(response.result || {}));
  } catch (error: any) {
    yield put(addQuestionFailure(error.message));
  }
}

// API Add Child Question
function* handleAddChildQuestion(action: PayloadAction<any>) {
  try {
    const payload = action.payload;
    const response: ApiResponse<Question> = yield call(apiRequestWithAuth, `/quiz/${payload.examId}/question`, "POST", payload);

    if (response.messageCode != 200) throw new Error("Failed to add question");

    yield put(addChildQuestionSuccess(response.result || {}));
  } catch (error: any) {
    yield put(addChildQuestionFailure(error.message));
  }
}

// API Update Question
function* handleUpdateQuestion(action: PayloadAction<{ id: string; examId: string; data: Partial<Question> }>) {
  try {
    const { id, examId, data } = action.payload;
    const response: ApiResponse<any> = yield call(apiRequestWithAuth, `/quiz/${examId}/question/${id}`, "PUT", data);

    if (response.messageCode != 200) throw new Error("Failed to update question");

    yield put(updateQuestionSuccess(response.result));
  } catch (error: any) {
    yield put(updateQuestionFailure(error.message));
  }
}

// API Update Child Question
function* handleUpdateChildQuestion(action: PayloadAction<{ id: string; examId: string; data: Partial<Question> }>) {
  try {
    const { id, examId, data } = action.payload;
    const response: ApiResponse<any> = yield call(apiRequestWithAuth, `/quiz/${examId}/question/${id}`, "PUT", data);

    if (response.messageCode != 200) throw new Error("Failed to update question");

    yield put(updateChildQuestionSuccess(response.result));
  } catch (error: any) {
    yield put(updateChildQuestionFailure(error.message));
  }
}

function* fetchQuestions(action: PayloadAction<{ examId: string;}>) {
  try {
    const response: ApiResponse<any> = yield call(apiRequestWithAuth, `/quiz/${action.payload.examId}`, "GET");
    if (response.messageCode == 200) {
      yield put(fetchQuestionsSuccess(response.result.questions || []));
      yield put(fetchExamSuccess(response.result || []));
    } else {
      yield put(fetchQuestionsSuccess([]));
      yield put(fetchQuestionsFailure("Failed to fetch users. Message: " + response.message));
    }
  } catch (error) {
    yield put(fetchQuestionsFailure((error as Error).message));
  }
}

function* addAnswerSaga(action: ReturnType<typeof addAnswerRequest>) {
  try {
    const response: ApiResponse<any> = yield call(
        apiRequestWithAuth,
        `/quiz/${action.payload.examId}/question/${action.payload.questionId}/add-answer`,
        'POST', action.payload
    );

    if (response.messageCode != 200) {
      throw new Error(response.message || "Update failed");
    }

    yield put(addAnswerSuccess({ questionId: action.payload.questionId, answer: response.result }));
  } catch (error: any) {
    yield put(addAnswerFailure(error.message));
  }
}

function* updateAnswerSaga(action: ReturnType<typeof updateAnswerRequest>) {
  try {
    const response: ApiResponse<any> = yield call(
        apiRequestWithAuth,
        `/quiz/${action.payload.examId}/question/${action.payload.questionId}/answer/${action.payload.answerId}`,
        'PUT', action.payload
    );

    if (response.messageCode != 200) {
      throw new Error(response.message || "Update failed");
    }

    yield put(updateAnswerSuccess({ questionId: action.payload.questionId, answer: response.result }));
  } catch (error: any) {
    yield put(updateAnswerFailure(error.message));
  }
}

function* updateChildAnswerSaga(action: ReturnType<typeof updateChildAnswerRequest>) {
  try {
    const response: ApiResponse<any> = yield call(
        apiRequestWithAuth,
        `/quiz/${action.payload.examId}/question/${action.payload.questionId}/answer/${action.payload.answerId}`,
        'PUT', action.payload
    );

    if (response.messageCode != 200) {
      throw new Error(response.message || "Update failed");
    }

    yield put(updateChildAnswerSuccess({ questionId: action.payload.questionId, answer: response.result }));
  } catch (error: any) {
    yield put(updateChildAnswerFailure(error.message));
  }
}

function* deleteAnswerSaga(action: ReturnType<typeof deleteAnswerRequest>) {
  try {
    const response: ApiResponse<any> = yield call(
        apiRequestWithAuth,
        `/quiz/${action.payload.examId}/question/${action.payload.questionId}/answer/${action.payload.answer.id}`,
        'DELETE', action.payload
    );

    console.log("Delete answer response:", response);

    if (response.messageCode != 200) {
      throw new Error(response.message || "Update failed");
    }

    yield put(deleteAnswerSuccess({ questionId: action.payload.questionId, answer: {id: action.payload.answer.id} }));
  } catch (error: any) {
    yield put(deleteAnswerFailure(error.message));
  }
}

// Watcher Saga
export function* watchQuestionActions() {
  yield takeLatest(deleteQuestionRequest.type, handleDeleteQuestion);
  yield takeLatest(deleteChildQuestionRequest.type, handleDeleteChildQuestion);
  yield takeLatest(addQuestionRequest.type, handleAddQuestion);
  yield takeLatest(addChildQuestionRequest.type, handleAddChildQuestion);
  yield takeLatest(fetchQuestionsRequest.type, fetchQuestions);
  yield takeLatest(updateQuestionRequest.type, handleUpdateQuestion);
  yield takeLatest(updateChildQuestionRequest.type, handleUpdateChildQuestion);
  yield takeLatest(addAnswerRequest.type, addAnswerSaga);
  yield takeLatest(updateAnswerRequest.type, updateAnswerSaga);
  yield takeLatest(updateChildAnswerRequest.type, updateChildAnswerSaga);
  yield takeLatest(deleteAnswerRequest.type, deleteAnswerSaga);
}
